import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useTheme } from '@mui/material/styles';
import { Grid, InputLabel, FormControl, Select, MenuItem, Typography, Paper, Box, Button } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { API_URL, gridSpacing } from 'utils/constants';
import MainCard from 'components/Cards/MainCard';
import SubCard from 'components/Cards/SubCard';
import ApiHelper from 'utils/apiHelper';
import { DataGridPro } from '@mui/x-data-grid-pro';
import AssetExports from './AssetExport';
import EditDeviceColumnsVisibilityDialog from '../Management/Devices/EditDeviceColumnsVisibility';
import { handleDeviceColumns, applyColumnStyle } from '../../utils/commonHelpers';

const ReportsAssetInventory = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [groupId, setGroupId] = useState(0);
    const [groupsData, setGroupsData] = useState([]);
    const [allAssetInventoryData, setAllAssetInventoryData] = useState([]);
    const [filtersData, setFiltersData] = useState([]);
    const gridRef = useRef();
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 25
    });
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [showEditDeviceOrderColumns, setShowEditDeviceOrderColumns] = useState(false);
    const [columnDefs, setColumnDefs] = useState([]);
    const [deviceColumnsData, setDeviceColumnsData] = useState([]);
    const [csvDownloadFileHeaders, setCsvDownloadFileHeaders] = useState([]);
    const [viewData, setViewData] = useState([]);
    const [viewId, setViewId] = useState(0);
    const [viewStatements, setViewStatements] = useState([]);
    const [selectedViewStatements, setSelectedViewStatements] = useState([]);
    const streamLoadingRef = useRef(false);
    const [streamLoading, setStreamLoading] = useState(false);

    const initialViewData = async (selectedItem) => {
        setLoading(true);
        try {
            // Get views list and statements
            const [views, viewStatements] = await Promise.all([
                ApiHelper.get('/api/management/views/'),
                ApiHelper.get('/api/management/get_view_statements/')
            ]);

            // Only proceed if we have views
            if (views.data.res.length > 0) {
                // Update views list and statements
                setViewData(views.data.res);
                setViewStatements(viewStatements.data);
            }
        } catch (err) {
            if (err.response?.status !== 401) {
                toast.error('Failed to fetch views data');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getGroups();
        getDeviceColumns();
        initialViewData();
        return () => {
            streamLoadingRef.current = false;
        };
    }, []);

    const getGroups = () => {
        ApiHelper.get('/api/reports/groups/')
            .then((res) => {
                setGroupsData(res.data);
            })
            .catch();
    };

    const handleSaveColumnOrders = () => {
        const columns_order = {};
        for (let i = 0; i < columnDefs.length; i++) {
            columns_order[columnDefs[i].field] = i + 1;
        }
        ApiHelper.put(`/api/management/device_columns/1/`, {
            status: 'device_columns_order',
            data: columns_order
        })
            .then((res) => {
                const [columns_order, columns_visibility] = handleDeviceColumns(res.data);
                setColumnDefs(
                    applyColumnStyle(
                        columns_order.map((c) => ({
                            ...c,
                            resizable: true,
                            headerClassName: 'device-column-header',
                            cellClassName: 'wrapCell'
                        }))
                    )
                );
                setDeviceColumnsData(columns_visibility);
                toast.success('Successfully saved.');
            })
            .catch(() => {
                toast.error('Failed to save the columns order');
            });
    };

    const handleChangeColumnOrder = (data) => {
        const { targetIndex, oldIndex } = data;
        const temp = columnDefs;
        const updatedVal = temp[oldIndex - 1];
        temp.splice(oldIndex - 1, 1);
        temp.splice(targetIndex - 1, 0, updatedVal);
        setColumnDefs(applyColumnStyle(temp));
        const headers = [];
        temp.map((data) => {
            headers.push({ label: data.headerName, key: data.field });
        });
        setCsvDownloadFileHeaders(headers);
    };

    const getInitialAsset = async () => {
        await getAssetInventoryData();
    };
    const getAssetInventoryData = async () => {
        // Get selected view statements
        const selectedStatements = viewStatements.filter((statement) => statement.view_id === viewId - 1);

        // Format statements for API call
        const statement = selectedStatements.map((statement, index) => ({
            id: index + 1,
            attribute: statement.deviceattrib,
            negative: statement.negative === 1,
            operator: statement.operator_id,
            value: statement.value,
            connector: index === selectedStatements.length - 1 ? '1' : statement.connector_id
            // operatorOptions: commonData.operators
        }));
        setSelectedViewStatements(statement);
        const timezoneOffSet = new Date().getTimezoneOffset();
        setLoading(true);
        const total_count = 0;
        const stream_limit = 1000;
        let stream_page = 0;
        streamLoadingRef.current = true;
        while (streamLoadingRef.current) {
            setStreamLoading(true);
            await ApiHelper.post(API_URL.ASSET_INVENTORY, {
                group_id: groupId === 0 ? [] : [groupId],
                statement: statement.length > 0 ? statement : [],
                page_number: stream_page,
                page_size: stream_limit,
                tz: -timezoneOffSet
            })
                .then((res) => {
                    const result = res.data.data;
                    setAllAssetInventoryData((prev) => [...prev, ...result]);
                    let groupName = 'Any';
                    let deviceTypeName = 'Any';
                    for (let i = 0; i < groupsData.length; i++) {
                        if (groupsData[i].id === groupId) {
                            groupName = groupsData[i].name;
                        }
                    }
                    const temp = [
                        { label: 'Group Name', value: groupName },
                        { label: 'Device Type', value: deviceTypeName }
                    ];
                    setFiltersData(temp);
                    setLoading(false);
                    stream_page++;
                    if (res.data.total < stream_page * stream_limit) {
                        setStreamLoading(false);
                        streamLoadingRef.current = false;
                    }
                })
                .catch(() => {
                    setAllAssetInventoryData([]);
                    toast.warning('There is no asset inventory data');
                    setLoading(false);
                });
        }
    };

    const getDeviceColumns = () => {
        ApiHelper.get('/api/management/device_columns/2/')
            .then((res) => {
                const [columns_order, columns_visibility] = handleDeviceColumns(res.data.devices);
                setColumnDefs(applyColumnStyle(columns_order));
                setDeviceColumnsData(columns_visibility);
                const temp = [];
                columns_order.map((data) => {
                    temp.push({ label: data.headerName, key: data.field });
                });
                setCsvDownloadFileHeaders(temp);
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.warn('There is device columns data');
                }
            });
    };
    const updatePaginationModel = (newPaginationModel) => {
        setPaginationModel(newPaginationModel);
    };

    const handleSelectionChange = (newSelection) => {
        setSelectedRows((prevSelected) => {
            // Create a new set to avoid modifying the previous state directly
            const updatedSelected = new Set(prevSelected);

            // Add selected rows from the current page
            newSelection.forEach((id) => {
                updatedSelected.add(id);
            });
            allAssetInventoryData.forEach((row) => {
                if (!newSelection.includes(row.deviceid)) {
                    updatedSelected.delete(row.deviceid);
                }
            });
            console.log(updatedSelected);
            return updatedSelected;
        });
    };

    // Function to get selected rows across all pages
    const getSelectedRows = () => {
        return allAssetInventoryData.filter((row) => selectedRows.has(row.deviceid));
    };

    const handleCloseEditDeviceColumnsDialog = () => {
        setShowEditDeviceOrderColumns(false);
    };

    const handleSubmitDeviceColumnsVisibility = (data) => {
        setShowEditDeviceOrderColumns(false);
        ApiHelper.put(`/api/management/device_columns/1/`, {
            status: 'device_column_visibility',
            data: data
        })
            .then((res) => {
                const [columns_order, columns_visibility] = handleDeviceColumns(res.data);
                setColumnDefs(applyColumnStyle(columns_order));
                setDeviceColumnsData(columns_visibility);
                toast.success('Successfully updated.');
                const temp = [];
                columns_order.map((data) => {
                    temp.push({ label: data.headerName, key: data.field });
                });
                setCsvDownloadFileHeaders(temp);
            })
            .catch(() => {
                toast.error('Failed to update the device columns.');
            });
    };

    return (
        <MainCard title="Operational Reports Management" cardHeaderTitle="Asset Inventory" cardHeaderShow={true}>
            <Grid container spacing={gridSpacing}>
                <Grid item xs={2} style={{ display: 'flex', flexDirection: 'column' }}>
                    <SubCard title="Filters">
                        <Grid container>
                            <Grid item xs={12}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select Group</InputLabel>
                                    <Select
                                        label="Select Group"
                                        value={groupId}
                                        name="group"
                                        onChange={(e) => setGroupId(Number(e.target.value))}
                                    >
                                        <MenuItem value={0}>Any</MenuItem>
                                        {groupsData.length > 0 &&
                                            groupsData.map((data, key) => (
                                                <MenuItem value={data.id} key={key}>
                                                    {data.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select View</InputLabel>
                                    <Select
                                        label="Select View"
                                        value={viewId}
                                        name="view"
                                        onChange={(e) => setViewId(Number(e.target.value))}
                                    >
                                        <MenuItem value={0}>Any</MenuItem>
                                        {viewData.length > 0 &&
                                            viewData.map((data, key) => (
                                                <MenuItem value={data.id + 1} key={key}>
                                                    {data.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <LoadingButton
                                    color="primary"
                                    variant="contained"
                                    sx={{ width: '100%' }}
                                    loading={loading}
                                    onClick={getInitialAsset}
                                >
                                    Submit
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </SubCard>
                </Grid>
                <Grid item xs={10}>
                    {loading ? (
                        <Typography sx={{ textAlign: 'center', mt: 3 }} color={theme.palette.secondary.main}>
                            Please wait, this could take some time...
                        </Typography>
                    ) : (
                        ''
                    )}
                    <Paper id="assetInventory">
                        {!loading && allAssetInventoryData.length > 0 && (
                            <Grid>
                                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        sx={{ margin: '5px', borderRadius: '6px' }}
                                        onClick={handleSaveColumnOrders}
                                    >
                                        Save Column Order
                                    </Button>
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        sx={{ margin: '5px', borderRadius: '6px' }}
                                        onClick={() => setShowEditDeviceOrderColumns(true)}
                                    >
                                        Show/Hide Columns
                                    </Button>
                                    {showEditDeviceOrderColumns && (
                                        <EditDeviceColumnsVisibilityDialog
                                            data={deviceColumnsData}
                                            onClose={handleCloseEditDeviceColumnsDialog}
                                            onSubmit={handleSubmitDeviceColumnsVisibility}
                                        />
                                    )}
                                    {!loading && allAssetInventoryData.length > 0 && (
                                        <AssetExports
                                            downloadFileName="asset_inventory"
                                            downloadFileOption="all"
                                            assetInfo={{ groupId, pageSize: 100, total: allAssetInventoryData.length }}
                                            selected={getSelectedRows()}
                                            headers={csvDownloadFileHeaders}
                                            selectedViewStatements={selectedViewStatements}
                                            allData={allAssetInventoryData}
                                            disable={streamLoading}
                                        />
                                    )}
                                </Grid>
                                <Box
                                    id="asset_devices"
                                    sx={{
                                        fontSize: '12px',
                                        '& .device-column-header': {
                                            color: 'black',
                                            fontWeight: 'bold'
                                        },
                                        height: 'calc(100vh - 248px)',
                                        width: '100%'
                                    }}
                                >
                                    <DataGridPro
                                        columns={columnDefs}
                                        rows={allAssetInventoryData}
                                        rowCount={allAssetInventoryData.length}
                                        rowHeight={30}
                                        ref={gridRef}
                                        getRowId={(row) => row.deviceid}
                                        pagination
                                        initialState={{
                                            pagination: {
                                                paginationModel
                                            }
                                        }}
                                        pageSizeOptions={[25, 100, 250, 500, 1000]}
                                        checkboxSelection
                                        disableColumnMenu
                                        onColumnOrderChange={handleChangeColumnOrder}
                                        slotProps={{
                                            pagination: {
                                                showFirstButton: true,
                                                showLastButton: true
                                            }
                                        }}
                                        onRowSelectionModelChange={handleSelectionChange}
                                        sortingMode="client"
                                        paginationMode="client"
                                        rowSelectionModel={Array.from(selectedRows)}
                                        loading={loading}
                                        onPaginationModelChange={updatePaginationModel}
                                        disableRowHeightAdjustment={false}
                                        getRowHeight={() => 30}
                                        sx={{
                                            '& .MuiDataGrid-columnHeaders': {
                                                backgroundColor: '#f5f5f5',
                                                position: 'sticky',
                                                top: 0,
                                                zIndex: 1
                                            },
                                            '& .MuiDataGrid-virtualScroller': {
                                                overflowY: 'auto',
                                                overflowX: 'auto'
                                            },
                                            '& .MuiDataGrid-columnHeaderTitleContainer': {
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                height: '100%'
                                            }
                                        }}
                                    />
                                </Box>
                            </Grid>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </MainCard>
    );
};

export default ReportsAssetInventory;
